import { useTranslation } from 'react-i18next';
import type { RequirementSlot } from '@infoscend-claims/shared';

export interface EvidenceItem {
  id: string;
  evidenceType: string;
  fileName: string;
  extractionStatus: string;
  extractionConfidence?: number | null;
  slotKey?: string;
}

interface DocumentRequirementsChecklistProps {
  slots: RequirementSlot[];
  evidence: EvidenceItem[];
  onAssign?: (evidenceId: string, slotKey: string) => void;
  targetSlotKey?: string;
  onSlotClick?: (slotKey: string) => void;
}

const NECESSITY_ORDER: Record<string, number> = { REQUIRED: 0, RECOMMENDED: 1, OPTIONAL: 2 };

function buildSlotAssignmentMap(slots: RequirementSlot[], evidence: EvidenceItem[]): Map<string, EvidenceItem | null> {
  // Sort slots by priority so REQUIRED slots claim evidence first
  const sorted = [...slots].sort(
    (a, b) => (NECESSITY_ORDER[a.necessity] ?? 2) - (NECESSITY_ORDER[b.necessity] ?? 2),
  );
  const consumedIds = new Set<string>();
  const map = new Map<string, EvidenceItem | null>();
  for (const slot of sorted) {
    const match = evidence.find(
      (e) =>
        !consumedIds.has(e.id) &&
        (e.slotKey === slot.slotKey || (slot.requiredEvidenceTypes as string[]).includes(e.evidenceType)),
    );
    if (match) {
      consumedIds.add(match.id);
      map.set(slot.slotKey, match);
    } else {
      map.set(slot.slotKey, null);
    }
  }
  return map;
}

function slotStatusFromItem(match: EvidenceItem | null): 'empty' | 'uploading' | 'extracting' | 'done-high' | 'done-low' | 'failed' {
  if (!match) return 'empty';
  if (match.extractionStatus === 'PENDING') return 'uploading';
  if (match.extractionStatus === 'EXTRACTING') return 'extracting';
  if (match.extractionStatus === 'FAILED') return 'failed';
  if (match.extractionStatus === 'COMPLETED') {
    const conf = match.extractionConfidence ?? 0;
    return Number(conf) >= 0.7 ? 'done-high' : 'done-low';
  }
  return 'empty';
}

const STATUS_ICONS: Record<string, string> = {
  empty: '○',
  uploading: '↑',
  extracting: '↻',
  'done-high': '✓',
  'done-low': '⚠',
  failed: '✗',
};

const STATUS_COLORS: Record<string, string> = {
  empty: 'text-slate-400',
  uploading: 'text-slate-500',
  extracting: 'text-copper',
  'done-high': 'text-success',
  'done-low': 'text-copper',
  failed: 'text-danger',
};

const NECESSITY_COLORS: Record<string, string> = {
  REQUIRED: 'bg-danger/10 text-danger',
  RECOMMENDED: 'bg-copper-soft text-copper-deep',
  OPTIONAL: 'bg-slate-100 text-slate-500',
};

export function DocumentRequirementsChecklist({ slots, evidence, onAssign: _onAssign, targetSlotKey, onSlotClick }: DocumentRequirementsChecklistProps) {
  const { t } = useTranslation();
  const assignmentMap = buildSlotAssignmentMap(slots, evidence);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {slots.map((slot) => {
        const matchedEvidence = assignmentMap.get(slot.slotKey) ?? null;
        const status = slotStatusFromItem(matchedEvidence);
        const isTarget = targetSlotKey === slot.slotKey;

        return (
          <div
            key={slot.slotKey}
            role={onSlotClick ? 'button' : undefined}
            tabIndex={onSlotClick ? 0 : undefined}
            onClick={() => onSlotClick?.(slot.slotKey)}
            onKeyDown={(e) => e.key === 'Enter' && onSlotClick?.(slot.slotKey)}
            className={[
              'p-3 border rounded transition-colors',
              status === 'done-high' ? 'border-success/30 bg-success/5' : 'border-hairline bg-bone',
              isTarget ? 'ring-2 ring-copper ring-offset-1' : '',
              onSlotClick ? 'cursor-pointer hover:border-copper/50' : '',
            ].join(' ')}
          >
            <div className="flex items-start gap-2 mb-1.5">
              <span className={['text-base leading-5 shrink-0', STATUS_COLORS[status]].join(' ')}>
                {STATUS_ICONS[status]}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-ink leading-tight">
                  {t(slot.labelI18nKey, { defaultValue: slot.slotKey })}
                </p>
              </div>
            </div>
            <span className={['text-[9px] font-semibold px-1.5 py-0.5 rounded-sm uppercase tracking-wide', NECESSITY_COLORS[slot.necessity]].join(' ')}>
              {t(`wizard.necessity.${slot.necessity}`)}
            </span>
            {matchedEvidence && (
              <p className="text-[10px] text-slate-500 mt-1.5 truncate">{matchedEvidence.fileName}</p>
            )}
            {!matchedEvidence && isTarget && (
              <p className="text-[10px] text-copper mt-1.5">{t('wizard.labels.targetSlot')}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
